/*
 * Copyright (c) 2024. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import {Package} from "types/entities/Package";
import {useTranslation} from "react-i18next";
import {Steps} from "intro.js-react";
import React, {useCallback} from "react";
import {ArrowLeftIcon} from "@heroicons/react/24/outline";

const useStepsHelper = (selectedPackage, stepsEnabled, setStepsEnabled, setSelectedTimeslotHandler, resetCustomSelectedTimeslotHandler) => {
    const {t} = useTranslation();
    const stepsForTimeslotCount = (count: number): any[] => {
        const helpTextClassName = "text-sm leading-4 tracking-tight font-normal";
        const HelpItem = (props: {text: string}) => {
            return (
                <div className={"flex items-center justify-center"}>
                    <ArrowLeftIcon className={"w-3 h-3"} />
                    <span className={helpTextClassName}>{props.text}</span>
                </div>
            );
        };
        const introItem = (
            <div className={"flex flex-col space-y-2 justify-center"}>
                <span>{t("Select the first slot, with the time that is suites you.")}</span>
                <div className={"grid grid-cols-[1fr,2fr] space-x-2"}>
                    <div
                        className="Timeslot border border-solid border-gray-800 bg-white rounded-lg relative cursor-pointer text-center p-2 py-3 sm:py-4 sm:p-4 text-base sm:text-lg font-bold flex flex-col items-center"
                        style={{
                            maxWidth: "120px",
                        }}
                    >
                        <div className="bg-yellow-400 w-full absolute z-1 inset-x-0 bottom-0 bg-opacity-30" style={{height: "50%"}} />
                        <div className="flex-grow w-full min-w-[80px] z-10 relative">
                            <span>14:00</span>
                        </div>
                        <div className="text-xs z-10 flex flex-row items-center justify-between relative">
                            <div className="inline-flex">
                                <span>4</span>
                                <div className="text-gray-800/70">/</div>
                                <span>8</span>
                            </div>
                            <svg
                                aria-hidden="true"
                                className="ml-1 w-3 h-3 object-cover"
                                fill="currentColor"
                                viewBox="0 0 24 24"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path d="M4.5 6.375a4.125 4.125 0 118.25 0 4.125 4.125 0 01-8.25 0zM14.25 8.625a3.375 3.375 0 116.75 0 3.375 3.375 0 01-6.75 0zM1.5 19.125a7.125 7.125 0 0114.25 0v.003l-.001.119a.75.75 0 01-.363.63 13.067 13.067 0 01-6.761 1.873c-2.472 0-4.786-.684-6.76-1.873a.75.75 0 01-.364-.63l-.001-.122zM17.25 19.128l-.001.144a2.25 2.25 0 01-.233.96 10.088 10.088 0 005.06-1.01.75.75 0 00.42-.643 4.875 4.875 0 00-6.957-4.611 8.586 8.586 0 011.71 5.157v.003z"></path>
                            </svg>
                        </div>
                    </div>
                    <div className={"flex flex-col w-full min-w-[150px] space-y-2 justify-center items-start"}>
                        <HelpItem text={t("Start of timeslot")} />
                        <HelpItem text={t("Occupancy of slot")} />
                    </div>
                </div>
            </div>
        );

        switch (count) {
            case 1:
                return [
                    {
                        element: document.getElementById("timeslot-index-0") as HTMLElement,
                        intro: introItem,
                        // position: "right",
                        // tooltipPosition: "right",
                        // options: {
                        //     tooltipClass: "test",
                        //     tooltipPosition: "left",
                        // },
                    },
                ];
            case 2:
                return [
                    {
                        element: document.getElementById("timeslot-index-0") as HTMLElement,
                        intro: introItem,
                        // position: "right",
                        // tooltipPosition: "right",
                        // options: {
                        //     tooltipClass: "test",
                        //     tooltipPosition: "left",
                        // },
                    },
                    {
                        element: document.getElementById("timeslot-index-2") as HTMLElement,
                        intro: t("This is the next timeslot, that is preselected available for your reservation, to achieve the best experience possible."),
                        position: "right",
                        tooltipPosition: "right",
                        options: {
                            tooltipClass: "test",
                            tooltipPosition: "right",
                        },
                    },
                ];
            case 3:
                return [
                    {
                        element: document.getElementById("timeslot-index-0") as HTMLElement,
                        intro: introItem,
                    },
                    {
                        element: document.getElementById("timeslot-index-2") as HTMLElement,
                        intro: t("This is the next timeslot, that is preselected available for your reservation, to achieve the best experience possible."),
                    },
                    {
                        element: document.getElementById("timeslot-index-4") as HTMLElement,
                        intro: t("This is the last timeslot, that is preselected available for your reservation."),
                        position: "right",
                        tooltipPosition: "right",
                        options: {
                            tooltipClass: "test",
                            tooltipPosition: "right",
                        },
                    },
                ];
            default:
                return [
                    {
                        element: document.getElementById("timeslot-index-0") as HTMLElement,
                        intro: t("Select the first slot, with the time that is suites you."),
                        position: "right",
                        tooltipPosition: "right",
                        options: {
                            tooltipClass: "test",
                            tooltipPosition: "right",
                            position: "right",
                        },
                    },
                ];
        }
    };

    const stepsForHelp = (selectedPackage: Package | null) => {
        const intro: any[] = [
            {
                title: t("Welcome"),
                intro: t("This introduction will help you with timeslots selection."),
                position: "right",
                tooltipPosition: "right",
                options: {
                    tooltipPosition: "right",
                    position: "right",
                },
            },
            {
                element: document.getElementById("BookingTimeslots") as HTMLElement,
                intro: t("These are all the available slots"),
                tooltipPosition: "top",
                position: "top",
                options: {
                    tooltipPosition: "top",
                    position: "top",
                },
            },
        ];

        const outro: any[] = [
            {
                element: document.getElementById("timeslot-possible-index-1") as HTMLElement,
                intro: t("You can choose a different timeslot for your start."),
                tooltipPosition: "top",
                position: "top",
                options: {
                    tooltipPosition: "top",
                    position: "top",
                },
            },
            {
                title: t("Ready"),
                intro: t("All set! If you want to repeat this help process, click here..."),
                position: "left",
                options: {
                    tooltipPosition: "left",
                    position: "left",
                },
            },
        ];

        return [...intro, ...stepsForTimeslotCount(selectedPackage ? selectedPackage.typical_timeslot_count : 0), ...outro];
    };

    const onStepsStartHandler = () => {},
        onStepsExitHandler = () => {
            setStepsEnabled(false);
            setSelectedTimeslotHandler(null);
            resetCustomSelectedTimeslotHandler();
        },
        onBeforeChangeHandler = (stepIndex, el) => {
            if (stepIndex === 2) {
                const elementRect = el.getBoundingClientRect();
                // Calculate the vertical middle of the screen
                const screenHeight = window.innerHeight;
                const middleOfScreen = screenHeight / 2;

                // Calculate the scroll position to bring the element to the middle of the screen
                const scrollPosition = elementRect.top + elementRect.height / 2 - middleOfScreen;
                window.scrollTo({
                    top: scrollPosition,
                    behavior: "smooth", // You can use 'auto' for instant scrolling or 'smooth' for smooth scrolling
                });
            }
        },
        onStepsChangeHandler = (stepIndex, el) => {
            console.log("onStepsChangeHandler", stepIndex, el);
            //stepIndex 2 is reserved for selecting a timeslot to showcase the selection
            if (stepIndex === 2) {
                const firstSlot = document.getElementById("timeslot-index-0");
                if (firstSlot) firstSlot.click();
            }
        },
        steps = stepsForHelp(selectedPackage as Package);

    const StepsComponent = useCallback(
        ({stepsEnabled}) => {
            return (
                <Steps
                    enabled={stepsEnabled}
                    initialStep={0}
                    options={{
                        // dontShowAgainLabel: t("Don't show this again"),
                        // dontShowAgain: true,
                        tooltipClass: "testTooltip",
                    }}
                    steps={steps}
                    onBeforeChange={onBeforeChangeHandler}
                    onChange={onStepsChangeHandler}
                    onExit={onStepsExitHandler}
                    onStart={onStepsStartHandler}
                />
            );
        },
        [stepsEnabled]
    );

    return {
        StepsComponent,
    };
};

export default useStepsHelper;
